import { Injectable } from '@angular/core';
import {
  ParkingShiftDetail,
  ParkingShiftDetailDto,
  ParkingShiftTableResponseDto,
  ParkingShiftTableRow,
  ParkingShiftTableRowDto,
} from '../../models/finance/parking-shift.model';

@Injectable({ providedIn: 'root' })
export class MapperParkingShiftService {
  mapTable(dto: ParkingShiftTableResponseDto | unknown): {
    items: ParkingShiftTableRow[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  } {
    const raw = dto as any;
    const data: ParkingShiftTableRowDto[] = raw?.data ?? raw?.Data ?? [];
    const total = Number(raw?.total ?? raw?.Total ?? 0);
    const page = Number(raw?.page ?? raw?.Page ?? 1);
    const pageSize = Number(raw?.pageSize ?? raw?.PageSize ?? 20);
    const totalPages = Number(raw?.totalPages ?? raw?.TotalPages ?? Math.max(1, Math.ceil(total / pageSize)));

    return {
      items: data.map((item) => this.mapRow(item)),
      total,
      page,
      pageSize,
      totalPages,
    };
  }

  mapRow(dto: ParkingShiftTableRowDto | any): ParkingShiftTableRow {
    return {
      id: Number(dto.id ?? dto.Id ?? 0),
      shiftCode: String(dto.shiftCode ?? dto.ShiftCode ?? ''),
      siteId: Number(dto.siteId ?? dto.SiteId ?? 0),
      siteName: String(dto.siteName ?? dto.SiteName ?? ''),
      openUserId: dto.openUserId ?? dto.OpenUserId ?? null,
      openUserName: String(dto.openUserName ?? dto.OpenUserName ?? ''),
      openDate: dto.openDate ?? dto.OpenDate ?? null,
      closeDate: dto.closeDate ?? dto.CloseDate ?? null,
      isOpen: Boolean(dto.isOpen ?? dto.IsOpen ?? false),
      systemTotalAmount: Number(dto.systemTotalAmount ?? dto.SystemTotalAmount ?? 0),
      declaredCash: dto.declaredCash ?? dto.DeclaredCash ?? null,
      declaredQr: dto.declaredQr ?? dto.DeclaredQr ?? null,
    };
  }

  mapDetail(dto: ParkingShiftDetailDto | any): ParkingShiftDetail {
    const declared = dto.declared ?? dto.Declared ?? {};
    const system = dto.system ?? dto.System ?? {};
    const byFeeType = system.byFeeType ?? system.ByFeeType ?? [];
    const variance = dto.variance ?? dto.Variance ?? null;
    const finances = dto.finances ?? dto.Finances ?? [];

    return {
      id: Number(dto.id ?? dto.Id ?? 0),
      shiftCode: String(dto.shiftCode ?? dto.ShiftCode ?? ''),
      siteId: Number(dto.siteId ?? dto.SiteId ?? 0),
      siteName: String(dto.siteName ?? dto.SiteName ?? ''),
      openUserId: dto.openUserId ?? dto.OpenUserId ?? null,
      openUserName: String(dto.openUserName ?? dto.OpenUserName ?? ''),
      closeUserId: dto.closeUserId ?? dto.CloseUserId ?? null,
      closeUserName: String(dto.closeUserName ?? dto.CloseUserName ?? ''),
      openDate: dto.openDate ?? dto.OpenDate ?? null,
      closeDate: dto.closeDate ?? dto.CloseDate ?? null,
      isOpen: Boolean(dto.isOpen ?? dto.IsOpen ?? false),
      declared: {
        cash: declared.cash ?? declared.Cash ?? null,
        qr: declared.qr ?? declared.Qr ?? null,
        money: declared.money ?? declared.Money ?? null,
        billing: declared.billing ?? declared.Billing ?? null,
        eWallet: declared.eWallet ?? declared.EWallet ?? null,
        credit: declared.credit ?? declared.Credit ?? null,
        giftCards: declared.giftCards ?? declared.GiftCards ?? null,
        cashierCash: declared.cashierCash ?? declared.CashierCash ?? null,
      },
      system: {
        totalAmount: Number(system.totalAmount ?? system.TotalAmount ?? 0),
        totalFee: Number(system.totalFee ?? system.TotalFee ?? 0),
        totalVat: Number(system.totalVat ?? system.TotalVat ?? 0),
        receiptCount: Number(system.receiptCount ?? system.ReceiptCount ?? 0),
        byFeeType: (byFeeType as any[]).map((x) => ({
          feeTypeId: x.feeTypeId ?? x.FeeTypeId ?? null,
          feeTypeName: String(x.feeTypeName ?? x.FeeTypeName ?? ''),
          totalAmount: Number(x.totalAmount ?? x.TotalAmount ?? 0),
          totalFee: Number(x.totalFee ?? x.TotalFee ?? 0),
          totalVat: Number(x.totalVat ?? x.TotalVat ?? 0),
          count: Number(x.count ?? x.Count ?? 0),
        })),
      },
      variance: variance
        ? {
            cash: variance.cash ?? variance.Cash ?? null,
            qr: variance.qr ?? variance.Qr ?? null,
            note: variance.note ?? variance.Note ?? '',
          }
        : null,
      finances: (finances as any[]).map((x) => ({
        id: Number(x.id ?? x.Id ?? 0),
        receiptId: x.receiptId ?? x.ReceiptId ?? null,
        feeTypeId: x.feeTypeId ?? x.FeeTypeId ?? null,
        feeTypeName: String(x.feeTypeName ?? x.FeeTypeName ?? ''),
        fee: Number(x.fee ?? x.Fee ?? 0),
        vat: Number(x.vat ?? x.Vat ?? 0),
        amount: Number(x.amount ?? x.Amount ?? 0),
        paidDate: x.paidDate ?? x.PaidDate ?? null,
        name: x.name ?? x.Name ?? null,
      })),
    };
  }
}
